import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, query, limit, getDocs, where, deleteDoc, doc, getDoc } from "firebase/firestore";
import { formatTimestamp } from "../../utils/dateFormat";
import { useLanguage } from "../../context/LanguageContext";
import {
  Bell, AlertTriangle, ClipboardCheck, CheckCircle,
  XCircle, ArrowRight, ArrowLeft, MapPin, Clock,
  Trash2, X, Navigation,
} from "lucide-react";

function getAlertBadge(type) {
  const map = {
    "speed_alert":         { label: "Speeding",       color: "bg-red-100 text-red-700"      },
    "long_stop_alert":     { label: "Long Stop",       color: "bg-orange-100 text-orange-700"},
    "no_visits_alert":     { label: "No Visits",       color: "bg-yellow-100 text-yellow-700"},
    "outside_hours_alert": { label: "Outside Hours",   color: "bg-purple-100 text-purple-700"},
    "zone_alert":          { label: "Wrong Zone",      color: "bg-blue-100 text-blue-700"    },
    "checkin":             { label: "Check-in",        color: "bg-green-100 text-green-700"  },
    "visit":               { label: "Visit",           color: "bg-teal-100 text-teal-700"    },
  };
  return map[type] || { label: type, color: "bg-gray-100 text-gray-600" };
}

function NotificationsPage({ companyId }) {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState("all");
  const [confirmClear, setConfirmClear]   = useState(false);
  const [clearing, setClearing]           = useState(false);
  const [pageSize]                        = useState(50);
  const [error, setError]                 = useState("");
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");
  const [notifyCheckins, setNotifyCheckins]   = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadNotifications(); }, []);

  async function loadNotifications() {
    setLoading(true);
    try {
      const configSnap = await getDoc(doc(db, "companies", companyId, "config", "settings"));
      let tz = "Asia/Dubai";
      let showCheckins = true;
      if (configSnap.exists()) {
        const cfg = configSnap.data();
        tz = cfg.timezone || "Asia/Dubai";
        showCheckins = cfg.notify_checkins !== false;
      }
      setCompanyTimezone(tz);
      setNotifyCheckins(showCheckins);

      // Build event types array based on settings
      const baseTypes = ["speed_alert", "long_stop_alert", "no_visits_alert", "outside_hours_alert", "zone_alert", "visit", "session_start", "session_end"];
      const eventTypes = showCheckins ? [...baseTypes, "checkin"] : baseTypes;

      const snap = await getDocs(query(
        collection(db, "companies", companyId, "events"),
        where("type", "in", eventTypes),
        limit(200)
      ));

      // Sort by timestamp descending in JS to avoid composite index requirement
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const ta = a.timestamp?.toMillis?.() ?? 0;
        const tb = b.timestamp?.toMillis?.() ?? 0;
        return tb - ta;
      });
      setNotifications(docs);
    } catch (err) {
      console.error("Failed to load notifications:", err.message);
    }
    setLoading(false);
  }

  async function clearAll() {
    setClearing(true);
    setError("");
    try {
      const snap = await getDocs(
        collection(db, "companies", companyId, "events")
      );
      // Delete in batches of 10
      const ids = snap.docs.map(d => d.id);
      for (let i = 0; i < ids.length; i += 10) {
        await Promise.all(
          ids.slice(i, i + 10).map(id =>
            deleteDoc(doc(db, "companies", companyId, "events", id))
          )
        );
      }
      setNotifications([]);
      setConfirmClear(false);
    } catch (err) {
      setError(`Failed to clear: ${err.message} (${err.code})`);
      console.error("Clear failed:", err.message);
    }
    setClearing(false);
  }

  async function deleteOne(id) {
    setError("");
    try {
      await deleteDoc(doc(db, "companies", companyId, "events", id));
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      setError(`Failed to delete: ${err.message} (${err.code})`);
    }
  }

  function formatTime(ts) {
    return formatTimestamp(ts, companyTimezone);
  }

  const typeStyles = {
    visit:         { bg: "bg-teal-100",   text: "text-teal-700",   border: "border-teal-200",   Icon: ClipboardCheck, label: "Visit"         },
    checkin:       { bg: "bg-green-100",  text: "text-green-700",  border: "border-green-200",  Icon: CheckCircle,    label: "Check-in"      },
    zone_alert:    { bg: "bg-red-100",    text: "text-red-700",    border: "border-red-200",    Icon: AlertTriangle,  label: "Zone Alert"    },
    session_start: { bg: "bg-blue-100",   text: "text-blue-700",   border: "border-blue-200",   Icon: CheckCircle,    label: "Session Start" },
    session_end:   { bg: "bg-gray-100",   text: "text-gray-600",   border: "border-gray-200",   Icon: XCircle,        label: "Session End"   },
    zone_entry:    { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", Icon: ArrowRight,     label: "Zone Entry"    },
    zone_exit:     { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", Icon: ArrowLeft,      label: "Zone Exit"     },
  };

  function getMessage(n) {
    const vendor = n.vendor_name || n.vendorName || "Unknown vendor";
    const client = n.client_name || n.clientName || "a client";
    if (n.type === "visit")         return `${vendor} visited ${client}`;
    if (n.type === "checkin")       return `${vendor} checked in at ${client}`;
    if (n.type === "zone_alert")    return `${vendor} triggered an out-of-zone alert in ${n.sectionName || "unknown zone"}`;
    if (n.type === "session_start") return `${vendor} started their working session`;
    if (n.type === "session_end")   return `${vendor} ended their session${n.duration ? ` after ${Math.round(n.duration / 3600)}h` : ""}`;
    if (n.type === "zone_entry")    return `${vendor} entered zone ${n.sectionName || "unknown"}`;
    if (n.type === "zone_exit")     return `${vendor} exited zone ${n.sectionName || "unknown"}`;
    return `${vendor} — ${n.type}`;
  }

  // Count per type for filter badges
  const counts = {};
  notifications.forEach(n => { counts[n.type] = (counts[n.type] || 0) + 1; });
  const alertCount = counts["zone_alert"] || 0;

  // Build filter tabs dynamically based on what events exist + notify_checkins setting
  const NOTIF_TYPES = [
    { id: "all",        label: t("notif_filter_all") },
    { id: "zone_alert", label: t("lbl_zone_alerts") },
    { id: "speed_alert",         label: t("notif_speed")          },
    { id: "long_stop_alert",     label: t("notif_long_stop")      },
    { id: "no_visits_alert",     label: t("notif_no_visits")      },
    { id: "outside_hours_alert", label: t("notif_outside_hours")  },
    // "Visits" tab counts both visit + checkin events
    { id: "visits_and_checkins", label: t("notif_filter_visits")  },
    ...(notifyCheckins ? [{ id: "checkin", label: t("notif_checkin") }] : []),
    { id: "session_start", label: t("events_session_start") },
    { id: "session_end",   label: t("events_session_end")   },
  ];

  // "visits_and_checkins" tab shows both visit and checkin types
  const filtered = filter === "all"
    ? notifications
    : filter === "visits_and_checkins"
    ? notifications.filter(n => n.type === "visit" || n.type === "checkin")
    : notifications.filter(n => n.type === filter);

  function getTabCount(tabId) {
    if (tabId === "all") return notifications.length;
    if (tabId === "visits_and_checkins") return (counts["visit"] || 0) + (counts["checkin"] || 0);
    return counts[tabId] || 0;
  }

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("notif_loading")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-800">{t("notif_title")}</h2>
              {alertCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {alertCount} {t("notif_filter_alerts").toLowerCase()}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-1">{notifications.length} {t("events_total")}</p>
          </div>
          {notifications.length > 0 && (
            <button onClick={() => setConfirmClear(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium transition">
              <Trash2 className="w-3.5 h-3.5" /> {t("notif_clear_all")}
            </button>
          )}
        </div>
      </div>

      {/* Confirm clear */}
      {confirmClear && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex justify-between items-center">
          <p className="text-red-700 text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {t("notif_confirm_clear_msg")}
          </p>
          <div className="flex gap-2">
            <button onClick={clearAll} disabled={clearing}
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg disabled:opacity-50">
              {clearing ? t("notif_clearing") : t("yes_delete")}
            </button>
            <button onClick={() => setConfirmClear(false)}
              className="bg-white text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg border">
              {t("cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex gap-2 flex-wrap">
          {NOTIF_TYPES.map(tab => {
            const count = getTabCount(tab.id);
            return (
              <button key={tab.id} onClick={() => setFilter(tab.id)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition flex items-center gap-1.5 ${
                  filter === tab.id
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  filter === tab.id ? "bg-white text-green-700" : "bg-gray-200 text-gray-500"
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Notifications list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">{t("notif_empty")}</p>
          <p className="text-gray-400 text-sm mt-1">{t("overview_no_activity")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
          {filtered.slice(0, pageSize).map((notif) => {
            const style = typeStyles[notif.type] || typeStyles["visit"];
            const badge = getAlertBadge(notif.type);
            // Build Google Maps URL if lat/lng available
            const lat = notif.lat ?? notif.latitude;
            const lng = notif.lng ?? notif.longitude;
            const mapsUrl = (lat != null && lng != null)
              ? `https://www.google.com/maps?q=${lat},${lng}`
              : null;
            return (
              <div key={notif.id}
                className="flex items-start gap-4 p-4 hover:bg-gray-50 transition group">

                {/* Icon */}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${style.bg} ${style.text}`}>
                  <style.Icon className="w-4 h-4" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{getMessage(notif)}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>
                    {notif.sectionName && notif.type !== "zone_alert" && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{notif.sectionName}
                      </span>
                    )}
                    {notif.duration && notif.type === "visit" && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{Math.round(notif.duration / 60)}min
                      </span>
                    )}
                    {/* Google Maps link for alerts with coordinates */}
                    {mapsUrl && (notif.type === "speed_alert" || notif.type === "long_stop_alert" || notif.type === "zone_alert") && (
                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 underline underline-offset-2">
                        <Navigation className="w-3 h-3" />
                        {lat.toFixed(5)}, {lng.toFixed(5)}
                      </a>
                    )}
                  </div>
                  {notif.type === "speed_alert" && (
                    <p className="text-sm text-red-600 font-medium mt-1">
                      Speed: {notif.speed_recorded} km/h (limit: {notif.speed_limit} km/h — exceeded by {notif.speed_exceeded_by?.toFixed(1)} km/h)
                    </p>
                  )}
                  {notif.type === "long_stop_alert" && (
                    <p className="text-sm text-orange-600 mt-1">
                      Stopped for {notif.stopped_minutes} minutes at this location
                    </p>
                  )}
                  {notif.type === "no_visits_alert" && (
                    <p className="text-sm text-yellow-600 mt-1">
                      Was active all day but recorded zero client visits
                    </p>
                  )}
                </div>

                {/* Time + delete */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-400">{formatTime(notif.timestamp)}</span>
                  <button onClick={() => deleteOne(notif.id)}
                    className="text-gray-300 hover:text-red-500 transition p-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {filtered.length > pageSize && (
            <div className="p-4 text-center">
              <p className="text-xs text-gray-400">{t("clients_showing")} {pageSize} {t("clients_of")} {filtered.length}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
